import { Icon } from '@gitbook/spine-icons';
import { confirm, toast } from '@gitbook/spine-react';

import type { NavigateFunction } from '../../navigation';
import { apiClient } from '../../state';

/**
 * Open a confirmation dialog and, once confirmed, detach the style guide from
 * the given site, then land on its style guide creation page.
 *
 * Only the site's reference to the style guide is cleared — the `styleguide` space
 * itself is kept, so it can be re-attached or reused by another site later.
 */
export function confirmDetachStyleguide(params: {
    organizationId: string;
    siteId: string;
    siteTitle: string;
    navigate: NavigateFunction;
}) {
    const { organizationId, siteId, siteTitle, navigate } = params;

    void confirm({
        title: 'Detach style guide',
        description: `The AI agent will stop using this style guide for “${siteTitle}”. The style guide itself isn't deleted, and you can attach one again later.`,
        kind: 'danger',
        confirmLabel: 'Detach',
        confirmLeadingIcon: Icon.LinkBroken,
        onConfirm: async (_event, { preventClose }) => {
            await toast.catch(
                async () => {
                    await apiClient.sites.updateSiteById(organizationId, siteId, {
                        styleguide: null,
                    });
                    // The site no longer has a style guide, so send the user to the
                    // creation page to set up a new one.
                    navigate({
                        screen: {
                            type: 'styleguide_new',
                            organization: organizationId,
                            site: siteId,
                        },
                    });
                },
                'Error while detaching style guide',
                { onReject: preventClose }
            );
        },
    });
}
